import { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Alert, Button, Flex, Form } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Card } from "ui/Card"
import { Text, Title } from "ui/Typography"
import { spacings } from "themes/variables"
import { useNewConfirmModal } from "modules/modals/hooks/useNewConfirmModal"
import { connectPowerTranz, disconnectPowerTranz, getPowerTranzAccountStatus, verifyPowerTranz } from "store/payment-provider"
import { mapPowerTranzAccount } from "store/powertranzApi"
import { getUser } from "store/user"
import { getPowerTranzErrorCode, getPowerTranzErrorMessage, getVerificationOutcome, normalizePageSet } from "helpers/powertranz"
import { ACCOUNT_STATUSES, ERROR_CODES, VERIFICATION_OUTCOMES } from "constants/powertranz"
import StatusSummary from "../StatusSummary"
import StateAlert from "../StateAlert"
import CredentialsForm from "../CredentialsForm"

const SECRET_FIELDS = ["powertranz_id", "powertranz_password"]
const CONNECT_FIELDS = [...SECRET_FIELDS, "page_set", "page_name"]

const OUTCOME_ALERT_TYPES = {
  [VERIFICATION_OUTCOMES.VERIFIED]: "success",
  [VERIFICATION_OUTCOMES.PENDING]: "info",
  [VERIFICATION_OUTCOMES.FAILED]: "error",
  [VERIFICATION_OUTCOMES.INVALID_CREDENTIALS]: "error",
  [VERIFICATION_OUTCOMES.HOSTED_PAGE_INVALID]: "error",
  [VERIFICATION_OUTCOMES.PROVIDER_UNAVAILABLE]: "warning",
  [VERIFICATION_OUTCOMES.RETRIABLE]: "warning"
}

// The account payload of a connect/verify response ({ powertranz_account_data }).
const accountFromResponse = (response) => mapPowerTranzAccount(response?.powertranz_account_data ?? response)

const PowerTranzAccountCard = ({ account, isOwner }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { showConfirm, modal } = useNewConfirmModal()
  const isConnecting = useSelector((state) => state.paymentProvider.connectPowerTranz.loading)
  const isVerifying = useSelector((state) => state.paymentProvider.verifyPowerTranz.loading)
  const isDisconnecting = useSelector((state) => state.paymentProvider.disconnectPowerTranz.loading)
  // Blocks a second submit before the loading state has rendered (double click / Enter).
  const isRequestInFlightRef = useRef(false)
  // { action: "connect" | "verify", outcome, detail } of the last call, until the next one starts.
  const [result, setResult] = useState(null)
  const [isEditingCredentials, setIsEditingCredentials] = useState(false)

  const hasAccount = ACCOUNT_STATUSES.includes(account.connectionStatus)
  const isBusy = isConnecting || isVerifying || isDisconnecting
  const isFormVisible = isOwner && account.isEligible && (!hasAccount || isEditingCredentials)

  useEffect(() => {
    // Page Set / Page Name are not secret, so prefill them; the ID and password never are.
    form.setFieldsValue({ page_set: account.pageSet, page_name: account.pageName })
  }, [form, account.pageSet, account.pageName])

  const runRequest = async (action, request, onError) => {
    if (isRequestInFlightRef.current) return
    isRequestInFlightRef.current = true
    setResult(null)
    try {
      const response = await dispatch(request())
      if (action !== "disconnect") setResult({ action, outcome: getVerificationOutcome({ account: accountFromResponse(response) }) })
      return response
    } catch (error) {
      const outcome = getVerificationOutcome({ error })
      setResult({ action, outcome, detail: getPowerTranzErrorMessage(t, getPowerTranzErrorCode(error)) })
      onError?.(outcome, error)
      // A failed verify can turn the account invalid; the success responses already carry the account.
      dispatch(getPowerTranzAccountStatus()).catch(() => {})
      return null
    } finally {
      isRequestInFlightRef.current = false
      // Keep the rest of the app (GET /user) in step too.
      dispatch(getUser())
    }
  }

  // Ties a rejected connect to the fields it's about, so the error is announced with the field.
  const showFieldErrors = (outcome, error) => {
    // validation_failed: the API returns { message: { field: [messages] } } for the connect fields.
    const fieldMessages = getPowerTranzErrorCode(error) === ERROR_CODES.VALIDATION_FAILED ? error?.data?.message : null
    if (fieldMessages && typeof fieldMessages === "object") {
      form.setFields(
        CONNECT_FIELDS.filter((name) => fieldMessages[name]).map((name) => ({ name, errors: [].concat(fieldMessages[name]) }))
      )
    } else if (outcome === VERIFICATION_OUTCOMES.INVALID_CREDENTIALS) {
      const message = t("powertranz.errors.invalid_credentials")
      form.setFields(SECRET_FIELDS.map((name) => ({ name, errors: [message] })))
    } else if (outcome === VERIFICATION_OUTCOMES.HOSTED_PAGE_INVALID) {
      const message = t("powertranz.errors.hosted_page_not_found")
      form.setFields(["page_set", "page_name"].map((name) => ({ name, errors: [message] })))
    }
  }

  const handleConnect = async (values) => {
    const payload = {
      powertranz_id: values.powertranz_id.trim(),
      // Passwords can legitimately contain spaces; send exactly what was typed.
      powertranz_password: values.powertranz_password,
      page_set: normalizePageSet(values.page_set),
      page_name: values.page_name.trim()
    }
    // The password is cleared after every submission, whatever the outcome.
    form.resetFields(["powertranz_password"])
    const response = await runRequest("connect", () => connectPowerTranz(payload), showFieldErrors)
    if (response) {
      form.resetFields(SECRET_FIELDS)
      setIsEditingCredentials(false)
    }
  }

  const handleCancelEditing = () => {
    form.resetFields(SECRET_FIELDS)
    setIsEditingCredentials(false)
  }

  const handleVerify = () => runRequest("verify", verifyPowerTranz)

  const handleDisconnectConfirm = () => runRequest("disconnect", disconnectPowerTranz)

  const handleDisconnectRequest = () => {
    showConfirm(
      {
        message: (
          <>
            {t("powertranz.settings.disconnect.intro")}
            <br />
            {t("powertranz.settings.disconnect.new_payments")}
            <br />
            {t("powertranz.settings.disconnect.history")}
            <br />
            {t("powertranz.settings.disconnect.refunds")}
          </>
        ),
        confirmLabel: t("powertranz.settings.disconnect.confirm"),
        cancelLabel: t("global.cancel")
      },
      handleDisconnectConfirm
    )
  }

  // Disconnect has only the failure outcomes; anything unexpected falls back to that action's "failed".
  const resultTitle =
    result &&
    t(`powertranz.settings.outcome.${result.action}.${result.outcome}`, {
      defaultValue: t(`powertranz.settings.outcome.${result.action}.failed`)
    })
  // Specific outcomes already say why in the title; only a plain failure adds the API's reason.
  const resultDetail = result?.outcome === VERIFICATION_OUTCOMES.FAILED ? result.detail : undefined

  return (
    <>
      <Card>
        <Flex vertical gap={spacings.M}>
          <Flex vertical gap={spacings.XS}>
            <Title level={4}>{t("powertranz.settings.card_title")}</Title>
            <Text>{t("powertranz.settings.card_description")}</Text>
          </Flex>

          {/* Status and results are announced to screen readers as they change. */}
          <Flex vertical gap={spacings.S} aria-live="polite">
            <StatusSummary account={account} />
            <StateAlert account={account} />
            {result && (
              <Alert
                showIcon
                type={OUTCOME_ALERT_TYPES[result.outcome]}
                message={resultTitle}
                description={resultDetail}
                action={
                  result.action === "verify" && result.outcome === VERIFICATION_OUTCOMES.RETRIABLE ? (
                    <Button color="primary" variant="outlined" size="small" onClick={handleVerify} disabled={isBusy}>
                      {t("powertranz.settings.retry")}
                    </Button>
                  ) : undefined
                }
              />
            )}
          </Flex>

          {!isOwner && <Alert type="info" showIcon message={t("powertranz.settings.owner_only")} />}

          {isFormVisible && (
            <CredentialsForm
              form={form}
              isReconnect={hasAccount}
              isSubmitting={isConnecting}
              isDisabled={isBusy}
              onSubmit={handleConnect}
              onCancel={isEditingCredentials ? handleCancelEditing : undefined}
            />
          )}

          {isOwner && hasAccount && !isEditingCredentials && (
            <Flex wrap gap={spacings.S}>
              <Button className="pr-l pl-l" color="primary" variant="solid" size="large" onClick={handleVerify} loading={isVerifying} disabled={isBusy}>
                {t("powertranz.settings.verify")}
              </Button>
              <Button className="pr-l pl-l" color="primary" variant="outlined" size="large" onClick={() => setIsEditingCredentials(true)} disabled={isBusy}>
                {t("powertranz.settings.update_credentials")}
              </Button>
              <Button className="pr-l pl-l" color="danger" variant="outlined" size="large" onClick={handleDisconnectRequest} loading={isDisconnecting} disabled={isBusy}>
                {t("global.disconnect")}
              </Button>
            </Flex>
          )}
        </Flex>
      </Card>
      {modal}
    </>
  )
}

PowerTranzAccountCard.propTypes = {
  account: PropTypes.shape({
    isEligible: PropTypes.bool,
    connectionStatus: PropTypes.string,
    pageSet: PropTypes.string,
    pageName: PropTypes.string
  }).isRequired,
  // Only the guide owner manages the merchant account; staff see status only. The API enforces
  // this too (authorize_guide_owner!).
  isOwner: PropTypes.bool.isRequired
}

export default PowerTranzAccountCard
